import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import { authService } from '../services/auth.service';
import { CheckIcon, EyeIcon, EyeOffIcon, AlertCircleIcon } from 'lucide-react';
import { toast } from 'react-toastify';

const PASSWORD_REQUIRED_MESSAGE = 'Password is required';
const PASSWORD_VALIDATION_MESSAGE = 'Passwords must be at least 8 characters long and contain at least one uppercase letter, one number, and one special character';

export default function ResetPasswordPage() {
    const [searchParams] = useSearchParams();
    const location = useLocation();
    const navigate = useNavigate();
    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [errors, setErrors] = useState({ password: '', confirmPassword: '' });
    const [isSuccess, setIsSuccess] = useState(false);
    const [isExpired, setIsExpired] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [showPasswords, setShowPasswords] = useState({
        password: false,
        confirmPassword: false
    });

    const isSetPassword = location.pathname === '/set-password';
    const pageTitle = isSetPassword ? 'Set Password' : 'Reset Password';
    const actionText = isSetPassword ? 'Setting...' : 'Resetting...';
    const buttonText = isSetPassword ? 'Set Password' : 'Reset Password';

    useEffect(() => {
        const verifyToken = async () => {
            const token = searchParams.get('token');
            if (!token) {
                setIsExpired(true);
                return;
            }

            try {
                const response = await authService.verifyResetToken(token);
                if (response.code === 'LINK_EXPIRED') {
                    setIsExpired(true);
                }
            } catch (error: any) {
                console.error('Error verifying token:', error);
                setIsExpired(true);
            }
        };

        verifyToken();
    }, [searchParams]);

    const validateForm = () => {
        const newErrors = { password: '', confirmPassword: '' };

        if (!password.trim()) {
            newErrors.password = PASSWORD_REQUIRED_MESSAGE;
        } else {
            const hasUpperCase = /[A-Z]/.test(password);
            const hasLowerCase = /[a-z]/.test(password);
            const hasNumber = /\d/.test(password);
            const hasSpecialChar = /[!@#$%^&*(),.?":{}|<>]/.test(password);

            if (!hasUpperCase || !hasLowerCase || !hasNumber || !hasSpecialChar || password.length < 8) {
                newErrors.password = PASSWORD_VALIDATION_MESSAGE;
            }
        }

        if (!confirmPassword.trim()) {
            newErrors.confirmPassword = 'Confirm password is required';
        } else if (password !== confirmPassword) {
            newErrors.confirmPassword = 'Passwords do not match!';
        }

        setErrors(newErrors);
        return !newErrors.password && !newErrors.confirmPassword;
    };

    const handleResetPassword = async () => {
        
        if (validateForm()) {
            
            setIsLoading(true);
            const token = searchParams.get('token');

            if (!token) {
                
                setIsExpired(true);
                setIsLoading(false);
                return;
            }

            try {
                
                const response = await authService.resetPassword({
                    token,
                    password
                });

                
                if (response.message === "Password reset successfully") {
                    
                    setIsSuccess(true);
                }
            } catch (error: any) {
                
                if (error.message === "Password reset link has expired. Please request a new one." || 
                    (error.error === "Unauthorized" && error.statusCode === 401)) {
                    setIsExpired(true);
                } else {
                    toast.error(error.message || 'Failed to reset password. Please try again.');
                }
            } finally {
                setIsLoading(false);
            }
        } else {
            
        }
    };

    const renderSuccessMessage = () => (
        <div className="min-h-screen bg-primary relative flex items-center justify-center p-4">
            <div
                className="absolute inset-0 bg-cover bg-[position:0%_50%] mix-blend-luminosity opacity-[0.08] rotate-180 auth-background"
            />
            <div className="bg-white rounded-card shadow-lg w-full max-w-card p-card-padding pb-card-padding-bottom relative z-10">
                <div className="text-center w-full">
                    <div className="w-16 h-16 rounded-full border-2 border-[#E7B355] bg-white flex items-center justify-center mx-auto mb-4">
                        <CheckIcon className="h-8 w-8 text-[#E7B355]" />
                    </div>
                    <h1 className="text-heading font-heading text-text-primary mb-2">Success</h1>
                    <p className="text-text-secondary text-subtext font-normal mb-8">Your password has been changed</p>
                    <button
                        onClick={() => navigate('/login')}
                        className="w-full btn-primary"
                    >
                        Go back to Login
                    </button>
                </div>
            </div>
        </div>
    );

    const renderLinkExpiry = () => (
        <div className="min-h-screen bg-primary relative flex items-center justify-center p-4">
            <div
                className="absolute inset-0 bg-cover bg-[position:0%_50%] mix-blend-luminosity opacity-[0.08] rotate-180 auth-background"
            />
            <div className="bg-white rounded-card shadow-lg w-full max-w-card p-card-padding pb-card-padding-bottom relative z-10">
                <div className="text-center w-full">
                    <div className="w-16 h-16 rounded-full bg-white flex items-center justify-center mx-auto mb-4">
                        <AlertCircleIcon className="h-12 w-12 text-red-500" />
                    </div>
                    <h1 className="text-heading font-heading text-text-primary mb-2">Link Expired!</h1>
                    <p className="text-text-secondary text-subtext font-normal mb-8">This reset link has expired or is invalid.
                        Please request a new one</p>
                    <button
                        onClick={() => navigate('/forgot-password')}
                        className="w-full btn-primary"
                    >
                        Request New Reset Link
                    </button>

                    <p className="mt-4 text-center text-[16px] text-text-secondary">
                        Remember your password? <button id="reset-password-back" onClick={() => navigate('/login')} className="text-small text-text-secondary underline">Login</button>
                    </p>
                </div>
            </div>
        </div>
    );

    const renderResetPasswordForm = () => (
        <div className="min-h-screen bg-primary flex items-center justify-center p-4">
            <div
                className="absolute inset-0 bg-cover bg-[position:0%_50%] mix-blend-luminosity opacity-[0.08] rotate-180 auth-background"
            />
            <div className="bg-white rounded-card shadow-lg w-full max-w-card p-card-padding pb-card-padding-bottom relative z-10">
                <div className="flex flex-col items-center mb-8">
                    <h1 id="reset-password-title" className="text-heading font-heading text-text-primary text-center">{pageTitle}</h1>
                    <p id="reset-password-subtitle" className="text-text-secondary text-subtext font-normal">Enter your new password and confirm</p>
                </div>

                <form onSubmit={(e) => {
                    
                    e.preventDefault();
                }} className="space-y-4">
                    <div className="relative">
                        <label
                            htmlFor="reset-password-new"
                            className="block text-[14px] text-[#767A81] mb-1"
                        >
                            New Password
                        </label>
                        <input
                            id="reset-password-new"
                            type={showPasswords.password ? 'text' : 'password'}
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            className="w-full px-3 py-2 text-[16px] border border-border rounded-input text-body focus:outline-none focus:ring-1 focus:ring-primary focus:border-primary"
                            placeholder="Enter New Password"
                        />
                        <button
                            id="reset-password-new-toggle"
                            type="button"
                            className="absolute right-3 top-[45px] -translate-y-1/2"
                            onClick={() => setShowPasswords(prev => ({
                                ...prev,
                                password: !prev.password
                            }))}
                        >
                            {showPasswords.password ? (
                                <EyeOffIcon className="h-5 w-5 text-text-secondary" />
                            ) : (
                                <EyeIcon className="h-5 w-5 text-text-secondary" />
                            )}
                        </button>
                        {errors.password && (
                            <p id="reset-password-new-error" className="text-small text-red-600 mt-1 text-right">{errors.password}</p>
                        )}
                    </div>

                    <div className="relative">
                        <label
                            htmlFor="reset-password-confirm"
                            className="block text-[14px] text-[#767A81] mb-1"
                        >
                            Confirm Password
                        </label>
                        <input
                            id="reset-password-confirm"
                            type={showPasswords.confirmPassword ? 'text' : 'password'}
                            value={confirmPassword}
                            onChange={(e) => setConfirmPassword(e.target.value)}
                            className="w-full px-3 py-2 text-[16px] border border-border rounded-input text-body focus:outline-none focus:ring-1 focus:ring-primary focus:border-primary"
                            placeholder="Confirm New Password"
                        />
                        <button
                            id="reset-password-confirm-toggle"
                            type="button"
                            className="absolute right-3 top-[45px] -translate-y-1/2"
                            onClick={() => setShowPasswords(prev => ({
                                ...prev,
                                confirmPassword: !prev.confirmPassword
                            }))}
                        >
                            {showPasswords.confirmPassword ? (
                                <EyeOffIcon className="h-5 w-5 text-text-secondary" />
                            ) : (
                                <EyeIcon className="h-5 w-5 text-text-secondary" />
                            )}
                        </button>
                        {errors.confirmPassword && (
                            <p id="reset-password-confirm-error" className="text-small text-red-600 mt-1 text-right">{errors.confirmPassword}</p>
                        )}
                    </div>

                    <button
                        id="reset-password-submit"
                        type="submit"
                        onClick={handleResetPassword}
                        disabled={isLoading}
                        className="w-full btn-primary"
                    >
                        {isLoading ? actionText : buttonText}
                    </button>

                    <p className="mt-4 text-center text-[16px] text-text-secondary">
                        Remember your password? <button id="reset-password-back" onClick={() => navigate('/login')} className="text-small text-text-secondary underline">Back to login</button>
                    </p>
                </form>
            </div>
        </div>
    );

    const getComponentToRender = () => {
        if (isExpired) {
            return renderLinkExpiry();
        }
        if (isSuccess) {
            return renderSuccessMessage();
        }
        return renderResetPasswordForm();
    };

    return getComponentToRender();
}
